// Daily Farm Logbook Form Template
// Based on Appendix A: Daily Farm Logbook

const getDailyFarmLogbookFormTemplate = (t) => ({
  formType: 'daily-farm-logbook',
  title: {
    en: 'Daily Farm Logbook',
    th: 'สมุดบันทึกประจำวันฟาร์ม'
  },
  description: {
    en: 'Daily record of farming activities, weather conditions, equipment used, personnel involved, and any observations or issues',
    th: 'บันทึกประจำวันเกี่ยวกับกิจกรรมการเกษตร สภาพอากาศ อุปกรณ์ที่ใช้ บุคลากรที่เกี่ยวข้อง และข้อสังเกตหรือปัญหา'
  },
  fields: [
    // Document Information Section
    {
      name: 'documentNumber',
      label: {
        en: 'Document Number',
        th: 'เลขที่เอกสาร'
      },
      type: 'text',
      required: false,
      defaultValue: 'CUL-V1.0-APP-A-EN/TH',
      tooltip: {
        en: 'Document identifier for tracking and reference purposes',
        th: 'เลขที่เอกสารสำหรับการติดตามและการอ้างอิง'
      }
    },
    {
      name: 'version',
      label: {
        en: 'Version',
        th: 'เวอร์ชัน'
      },
      type: 'text',
      required: false,
      defaultValue: '1.0',
      tooltip: {
        en: 'Document version number for change control',
        th: 'เลขเวอร์ชันของเอกสารสำหรับควบคุมการเปลี่ยนแปลง'
      }
    },
    {
      name: 'effectiveDate',
      label: {
        en: 'Effective Date',
        th: 'วันที่มีผลบังคับ'
      },
      type: 'date',
      required: false,
      tooltip: {
        en: 'Date when this logbook entry becomes effective. Leave blank to use current date.',
        th: 'วันที่บันทึกสมุดรายวันนี้มีผลบังคับ ปล่อยว่างไว้เพื่อใช้วันที่ปัจจุบัน'
      }
    },
    {
      name: 'preparedBy',
      label: {
        en: 'Prepared By',
        th: 'จัดทำโดย'
      },
      type: 'text',
      required: true,
      placeholder: {
        en: 'Enter your name and title',
        th: 'ป้อนชื่อและตำแหน่งของคุณ'
      },
      tooltip: {
        en: 'Name and title of the person preparing this logbook entry',
        th: 'ชื่อและตำแหน่งของบุคคลที่จัดทำบันทึกสมุดรายวันนี้'
      }
    },
    {
      name: 'approvedBy',
      label: {
        en: 'Approved By',
        th: 'ได้รับการอนุมัติโดย'
      },
      type: 'text',
      required: true,
      placeholder: {
        en: 'Enter approver name and title',
        th: 'ป้อนชื่อและตำแหน่งของผู้อนุมัติ'
      },
      tooltip: {
        en: 'Name and title of the person approving this logbook entry',
        th: 'ชื่อและตำแหน่งของบุคคลที่อนุมัติบันทึกสมุดรายวันนี้'
      }
    },

    // Daily Log Entry Section
    {
      name: 'sectionHeader_dailyEntry',
      label: {
        en: '=== Daily Log Entry ===',
        th: '=== บันทึกประจำวัน ==='
      },
      type: 'header'
    },
    {
      name: 'entryDate',
      label: {
        en: 'Date (วันที่)',
        th: 'วันที่'
      },
      type: 'date',
      required: true,
      tooltip: {
        en: 'Date of the farming activities being logged. Format: YYYY-MM-DD',
        th: 'วันที่ของกิจกรรมการเกษตรที่กำลังบันทึก รูปแบบ: YYYY-MM-DD'
      }
    },
    {
      name: 'shift',
      label: {
        en: 'Shift (กะงาน)',
        th: 'กะงาน'
      },
      type: 'select',
      options: [
        { value: 'morning', label: { en: 'Morning Shift (06:00-14:00)', th: 'กะเช้า (06:00-14:00)' } },
        { value: 'afternoon', label: { en: 'Afternoon Shift (14:00-22:00)', th: 'กะบ่าย (14:00-22:00)' } },
        { value: 'night', label: { en: 'Night Shift (22:00-06:00)', th: 'กะดึก (22:00-06:00)' } },
        { value: 'other', label: { en: 'Other', th: 'อื่น ๆ' } }
      ],
      required: true,
      tooltip: {
        en: 'Work shift during which activities were performed',
        th: 'กะงานที่ดำเนินกิจกรรม'
      }
    },
    {
      name: 'weatherConditions',
      label: {
        en: 'Weather Conditions (สภาพอากาศ)',
        th: 'สภาพอากาศ'
      },
      type: 'text',
      required: true,
      placeholder: {
        en: 'Temperature, humidity, wind, precipitation...',
        th: 'อุณหภูมิ ความชื้น ลม ฝน...'
      },
      tooltip: {
        en: 'Weather conditions during the farming activities. Include temperature, humidity, wind speed/direction, precipitation, etc.',
        th: 'สภาพอากาศในช่วงกิจกรรมการเกษตร รวมถึงอุณหภูมิ ความชื้น ความเร็ว/ทิศทางลม ปริมาณฝน เป็นต้น'
      }
    },
    {
      name: 'activitiesPerformed',
      label: {
        en: 'Activities Performed (กิจกรรมที่ดำเนินการ)',
        th: 'กิจกรรมที่ดำเนินการ'
      },
      type: 'textarea',
      rows: 4,
      required: true,
      placeholder: {
        en: 'Describe all farming activities performed during the shift...',
        th: 'อธิบายกิจกรรมการเกษตรทั้งหมดที่ดำเนินการในกะงาน...'
      },
      tooltip: {
        en: 'Detailed description of all farming activities performed during the shift. Include planting, harvesting, watering, fertilizing, pest control, equipment maintenance, etc.',
        th: 'คำอธิบายโดยละเอียดของกิจกรรมการเกษตรทั้งหมดที่ดำเนินการในกะงาน รวมถึงการปลูก การเก็บเกี่ยว การรดน้ำ การใส่ปุ๋ย การควบคุมศัตรูพืช การบำรุงรักษาอุปกรณ์ เป็นต้น'
      }
    },
    {
      name: 'equipmentUsed',
      label: {
        en: 'Equipment Used (อุปกรณ์ที่ใช้)',
        th: 'อุปกรณ์ที่ใช้'
      },
      type: 'textarea',
      rows: 3,
      required: true,
      placeholder: {
        en: 'List all equipment and tools used...',
        th: 'ระบุอุปกรณ์และเครื่องมือที่ใช้ทั้งหมด...'
      },
      tooltip: {
        en: 'List all equipment, machinery, and tools used during the farming activities. Include equipment identification numbers if available.',
        th: 'ระบุอุปกรณ์ เครื่องจักร และเครื่องมือทั้งหมดที่ใช้ในกิจกรรมการเกษตร รวมถึงเลขประจำเครื่องถ้ามี'
      }
    },
    {
      name: 'personnelInvolved',
      label: {
        en: 'Personnel Involved (บุคลากรที่เกี่ยวข้อง)',
        th: 'บุคลากรที่เกี่ยวข้อง'
      },
      type: 'textarea',
      rows: 3,
      required: true,
      placeholder: {
        en: 'List all personnel involved with their roles...',
        th: 'ระบุบุคลากรที่เกี่ยวข้องทั้งหมดพร้อมบทบาท...'
      },
      tooltip: {
        en: 'List all personnel involved in the farming activities. Include names, roles, and responsibilities for each person.',
        th: 'ระบุบุคลากรทั้งหมดที่เกี่ยวข้องในกิจกรรมการเกษตร รวมถึงชื่อ บทบาท และหน้าที่ของแต่ละคน'
      }
    },
    {
      name: 'observationsIssues',
      label: {
        en: 'Observations / Issues (ข้อสังเกต / ปัญหา)',
        th: 'ข้อสังเกต / ปัญหา'
      },
      type: 'textarea',
      rows: 4,
      required: true,
      placeholder: {
        en: 'Note any observations, problems, or issues encountered...',
        th: 'หมายเหตุข้อสังเกต ปัญหา หรืออุปสรรคที่พบ...'
      },
      tooltip: {
        en: 'Document any observations about plant health, growth patterns, pest presence, equipment malfunctions, or other issues. Include any unusual events or deviations from standard procedures.',
        th: 'บันทึกข้อสังเกตเกี่ยวกับสุขภาพพืช รูปแบบการเจริญเติบโต การปรากฏตัวของศัตรูพืช ความผิดปกติของอุปกรณ์ หรือปัญหาอื่น ๆ รวมถึงเหตุการณ์ที่ผิดปกติหรือการเบี่ยงเบนจากขั้นตอนมาตรฐาน'
      }
    },
    {
      name: 'notes',
      label: {
        en: 'Notes (หมายเหตุ)',
        th: 'หมายเหตุ'
      },
      type: 'textarea',
      rows: 3,
      required: false,
      placeholder: {
        en: 'Additional notes or comments...',
        th: 'หมายเหตุหรือความคิดเห็นเพิ่มเติม...'
      },
      tooltip: {
        en: 'Any additional notes, comments, or information relevant to the farming activities. This could include planned activities for the next day, reminders, or special instructions.',
        th: 'หมายเหตุ ความคิดเห็น หรือข้อมูลเพิ่มเติมใด ๆ ที่เกี่ยวข้องกับกิจกรรมการเกษตร ซึ่งอาจรวมถึงกิจกรรมที่วางแผนไว้สำหรับวันถัดไป รายการเตือน หรือคำแนะนำพิเศษ'
      }
    }
  ]
});

export default getDailyFarmLogbookFormTemplate;